import { EventInput } from "@fullcalendar/core";
import { RRule } from "rrule";
import { DestinationIDSet } from "userful-chronos-app-common-js/dist/models/cdm/CdmGroup";
import { getSecondsFromDuration } from "userful-chronos-app-common-js/dist/models/common";
import { PassiveMapperComp } from "userful-chronos-app-common-js/dist/models/mapping/MappingGroups";
import { SchedulableEvent } from "userful-chronos-app-common-js/dist/models/schedule/event";
import { SchedulableID } from "userful-chronos-app-common-js/dist/models/schedule/schedulable";
import { Schedule, ICalSpecData } from "userful-chronos-app-common-js/dist/models/schedule/schedule";
import { getRRule, millisOrStringToDuration } from "./eventsUtils";

const MAX_OCC_COUNT = 30;
const SAME_EVENT_TIME_TOLERANCES = 5 * 1000; // 5 seconds tolerance

const findEventsUsingSchedulable = (events: SchedulableEvent[], schedulable: SchedulableID): SchedulableEvent[] =>
    events.filter((e) => e.schedulableID.value === schedulable.value);

const occurancesConflict = (left: Date[], right: Date[]): boolean => {
    for (const l of left) {
        for (const r of right) {
            const rms = r.getTime();
            const lms = l.getTime();
            const leftBound = rms - SAME_EVENT_TIME_TOLERANCES;
            const rightBound = rms + SAME_EVENT_TIME_TOLERANCES;
            if (lms < rightBound && lms > leftBound) {
                return true;
            }
        }
    }
    return false;
};

const destIDSetToStrings = (dest: DestinationIDSet): string[] =>
    [...dest.groups, ...dest.physicalAudio, ...dest.physicalVideo, ...dest.videoWalls].map((i) => i.value);

const destinationSetsHaveSameDestinations = (left: DestinationIDSet, right: DestinationIDSet) => {
    const leftIDs = destIDSetToStrings(left);
    const rightIDs = destIDSetToStrings(right);
    for (const id of leftIDs) {
        if (rightIDs.includes(id)) {
            return true;
        }
    }
    return false;
};

export const getConflictingEventsFromPassiveMappers = (
    currentPassiveMapper: PassiveMapperComp,
    allMappers: PassiveMapperComp[],
    allEvents: SchedulableEvent[],
    allSchedules: Schedule[],
    event: EventInput,
    timeZone?: string,
): SchedulableEvent[] => {
    const rrule = getRRule(event, timeZone);
    const durationInSeconds = getSecondsFromDuration(millisOrStringToDuration(event.duration));
    const schedulables = getSchedulablesWithSameDestination(currentPassiveMapper, allMappers);
    return schedulables
        .map((s) => getConflictingEvents(s, allEvents, allSchedules, rrule, durationInSeconds * 1000))
        .flatMap((m) => m);
};

const getSchedulablesWithSameDestination = (
    currentPassiveMapper: PassiveMapperComp,
    allMappers: PassiveMapperComp[],
): SchedulableID[] =>
    allMappers
        .filter(
            (m) =>
                m.id.value !== currentPassiveMapper.id.value &&
                destinationSetsHaveSameDestinations(currentPassiveMapper.destinations, m.destinations),
        )
        .map((m) => ({
            value: m.id.value,
            type: "MAPPER",
        }));

const getConflictingEvents = (
    schedulable: SchedulableID,
    allEvents: SchedulableEvent[],
    allSchedules: Schedule[],
    newRRule: RRule,
    newDuration: number,
): SchedulableEvent[] => {
    const eventsWithSameSchedulable = findEventsUsingSchedulable(allEvents, schedulable);
    // get the next future occurance of new schedule
    const nextNewOccurance = newRRule.after(new Date(), true);
    if (!nextNewOccurance) {
        return [];
    }
    return eventsWithSameSchedulable.reduce((acc, curr: SchedulableEvent) => {
        // find schedule first
        const foundSchedule = allSchedules.find((item) => item.id.value === curr.scheduleID.value);
        if (!foundSchedule || foundSchedule.scheduleData["@type"] !== "ical") {
            return acc;
        }

        // get the next future occurance of existing schedule
        const existingSchedule = foundSchedule.scheduleData as ICalSpecData;
        const existingRRule = RRule.fromString(existingSchedule.value);
        const nextExistingOccurance = existingRRule.after(new Date(), true);
        if (!nextExistingOccurance) {
            return acc;
        }
        const existingDuration = getSecondsFromDuration(existingSchedule.duration) * 1000;

        // find the latest common date to reduce comparisions e.g. if one of events happens every day, the other events happens once a year in 6 months
        const latestFirstOccurance = new Date(
            Math.max(nextExistingOccurance.getTime() - existingDuration, nextNewOccurance.getTime() - newDuration),
        );
        // get new schedule's occurances after latestFirstOccurance
        const newOccurances = getRRuleOccurances(newRRule, newDuration, latestFirstOccurance);
        // get existing schedule's occurances after latestFirstOccurance
        const existingOccurances = getRRuleOccurances(existingRRule, existingDuration, latestFirstOccurance);

        if (occurancesConflict(existingOccurances, newOccurances)) {
            acc.push(curr);
        }
        return acc;
    }, []);
};

// get at most MAX_OCC_COUNT number of occurances in the future of minDate
const getRRuleOccurances = (rrule: RRule, duration: number, minDate: Date): Date[] => {
    const nextOccurance = rrule.after(minDate, true);
    if (!nextOccurance) {
        // no future occurance
        return [];
    }
    const clonedFutureRule = new RRule({
        ...rrule.origOptions,
        dtstart: nextOccurance,
    });
    return clonedFutureRule
        .all((date, len) => len <= MAX_OCC_COUNT)
        .reduce((acc, curr) => {
            acc.push(curr);
            acc.push(new Date(curr.getTime() + duration));
            return acc;
        }, []);
};
